import { dt, VERSION_CLIENT } from '.';
import { SHAPE_STYLES } from './constants';
import { COLORS } from './constants-colors';
import logger from './controllers/logger';
import { getObjectCountByType } from './entity';
import { addV2, scaleV2, v2 } from './helpers';
import { ObjectType, type Side } from './shared/api_shared_types';
import {
  VisibilityState,
  RenderItemType,
  TextAlignHor,
  type Color,
  type GameManager,
  type GameObject,
  type LineStyle,
  type RenderShadow,
  type RenderShapeStyle,
  type Shape,
  type V2,
} from './types';

const defaultFontId = 'font_regular';

export type RenderPackageItem =
  | RenderPackageClipStart
  | RenderPackageClipStop
  | RenderPackageRect
  | RenderPackageClearRect
  | RenderPackageCircle
  | RenderPackageArc
  | RenderPackageText
  | RenderPackageImage
  | RenderPackageBezier
  | RenderPackageLine
  | RenderPackagePoint
  | RenderPackageShape
  | RenderPackageTooltipBubble;

export type RenderPackageTransform = {
  positionX: number;
  positionY: number;
  positionLocalX: number;
  positionLocalY: number;
  rotation: number;
  log: string;
  scale: number;
  opacity: number;
};

export const EMPTY_RENDER_TRANSFORM: RenderPackageTransform = {
  positionX: 0,
  positionY: 0,
  positionLocalX: 0,
  positionLocalY: 0,
  rotation: 0,
  scale: 1,
  opacity: 1,
  log: '',
} as const;

export type RenderPackageClipStart = {
  type: RenderItemType.clip_start;
};

export type RenderPackageClipStop = {
  type: RenderItemType.clip_stop;
};

export type RenderPackageRect = RenderPackageTransform & {
  type: RenderItemType.rect;
  width: number;
  height: number;
  cornerRadius1: number;
  cornerRadius2: number;
  cornerRadius3: number;
  cornerRadius4: number;
  shapeStyle: RenderShapeStyle;
  shadow: RenderShadow | null;
};

export type RenderPackageClearRect = RenderPackageTransform & {
  type: RenderItemType.clearRect;
  width: number;
  height: number;
  roundedCorners: boolean;
  cornerRadius1: number;
  cornerRadius2: number;
  cornerRadius3: number;
  cornerRadius4: number;
};

export type RenderPackageCircle = RenderPackageTransform & {
  type: RenderItemType.circle;
  radius: number;
  shapeStyle: RenderShapeStyle;
  shadow: RenderShadow | null;
};

export type RenderPackageArc = RenderPackageTransform & {
  type: RenderItemType.arc;
  radius: number;
  start: number;
  length: number;
  counterClockWise: boolean;
  shapeStyle: RenderShapeStyle;
  shadow: RenderShadow | null;
};

export type RenderPackageText = RenderPackageTransform & {
  type: RenderItemType.text;
  size: number;
  hAlign: TextAlignHor;
  text: string;
  fillColor: Color;
  strokeWeight: number;
  strokeColor: Color;
  shadow: RenderShadow | null;
  fontId: string;
};

export type RenderPackageImage = RenderPackageTransform & {
  type: RenderItemType.image;

  shape: Shape;
  radius: number;
  width: number;
  height: number;
  imageId: string;
  partial?: boolean;
  sourcePos?: V2;
  sourceWidth?: number;
  sourceHeight?: number;
  flipHorizontally: boolean;
};

export type RenderPackageLine = RenderPackageTransform & {
  type: RenderItemType.line;
  posA: V2;
  posB: V2;
  style: LineStyle;
};

export type RenderPackageBezier = RenderPackageTransform & {
  type: RenderItemType.bezier;
  posA: V2;
  cpA: V2;
  cpB: V2;
  posB: V2;
  style: LineStyle;
};

export type RenderPackagePoint = RenderPackageTransform & {
  type: RenderItemType.point;
  size: number;
  color: Color;
};

export type RenderPackageShape = RenderPackageTransform & {
  type: RenderItemType.shape;
  points: V2[];
  fill: boolean;
  close: boolean;
  shapeStyle: RenderShapeStyle;
  shadow: RenderShadow | null;
};

export type RenderPackageTooltipBubble = RenderPackageTransform & {
  type: RenderItemType.tooltip_bubble;
  width: number;
  height: number;
  cornerRadius: number;
  arrowSide: Side;
  arrowOffset: number;
  arrowWidth: number;
  arrowLength: number;
  shapeStyle: RenderShapeStyle;
  shadow: RenderShadow | null;
};

export function createRenderPackageFromHierarchy(
  object: GameObject,
  absolutePosition: V2,
  absoluteRotation: number,
  absoluteScale: number,
  absoluteOpacity: number,
): RenderPackageItem[] {
  let doRender =
    object.visibilityState !== VisibilityState.HIDDEN &&
    object.visibilityState !== VisibilityState.NONE;

  if (!doRender) return [];

  let renderItems: RenderPackageItem[] = [];

  const objectAbsPos = addV2(absolutePosition, scaleV2(object.pos, absoluteScale));
  const objectAbsScale = absoluteScale * object.scale;
  const objectAbsOpacity = absoluteOpacity * object.opacity;

  const controller = object.manager.objectControllerMap[object.objectType];
  if (controller && controller.createPackage) {
    const objectPackage = controller.createPackage(
      object,
      objectAbsPos,
      objectAbsScale,
      objectAbsOpacity,
    );
    renderItems.push(...objectPackage);
  }

  if (object.doClipChildren) {
    renderItems.push({ type: RenderItemType.clip_start });
  }

  object.children.sort((a, b) => {
    if (a.alwaysOnTop !== b.alwaysOnTop) {
      return a.alwaysOnTop ? 1 : -1;
    }
    return a.localZIndex - b.localZIndex;
  });

  for (let i = 0; i < object.children.length; i++) {
    const child = object.children[i];
    let childPackage = createRenderPackageFromHierarchy(
      child,
      objectAbsPos,
      0,
      objectAbsScale,
      objectAbsOpacity,
    );
    renderItems.push(...childPackage);
  }

  if (object.doClipChildren) {
    renderItems.push({ type: RenderItemType.clip_stop });
  }

  return renderItems;
}

export function createDevInfoPackage(
  manager: GameManager,
  doRedraw: boolean,
  scale: number,
): RenderPackageItem[] {
  const rPackage: RenderPackageItem[] = [];

  const WIDTH = 300;
  const posX = 125 * scale;
  const position = v2(posX, scale * (manager.space.height - 43));

  const rect: RenderPackageRect = {
    type: RenderItemType.rect,
    ...EMPTY_RENDER_TRANSFORM,
    scale,
    positionX: position.x,
    positionY: position.y,
    width: WIDTH,
    height: 92,
    shapeStyle: {
      fillColor: COLORS.WHITE_100,
      strokeWeight: 0,
      strokeColor: COLORS.TRANSPARENT,
      lineDash: [],
      lineDashOffset: 0,
      shadow: null,
    },
    cornerRadius1: 0,
    cornerRadius2: 0,
    cornerRadius3: 0,
    cornerRadius4: 0,
    shadow: null,
  };
  rPackage.push(rect);

  const dtms = Math.round(dt * 1000);
  const align = TextAlignHor.RIGHT;
  const x = WIDTH - 40;

  rPackage.push(
    CreateDebugTextPackage(
      `dt: ${dtms} ms`,
      v2(scale * x, scale * (manager.space.height - 71)),
      scale,
      align,
    ),
  );

  const userName = `${manager.user.firstName} ${manager.user.lastName}`;
  const CMS = manager.user.cmsRole ? ' (debug)' : '';
  rPackage.push(
    CreateDebugTextPackage(
      userName + CMS,
      v2(scale * x, scale * (manager.space.height - 57)),
      scale,
      align,
    ),
  );

  rPackage.push(
    CreateDebugTextPackage(
      manager.user.email,
      v2(scale * x, scale * (manager.space.height - 43)),
      scale,
      align,
    ),
  );

  if (VERSION_CLIENT) {
    // " - " + 7-char hash ≈ 52px at 10px font_regular; bold count right-aligns flush against it
    const HASH_SUFFIX_WIDTH = 52;
    const clientParts = VERSION_CLIENT.split('-');

    const clientCount = clientParts[0];
    const clientHash = clientParts.length > 1 ? clientParts[1] : '';

    rPackage.push(
      CreateDebugTextPackage(
        'client: ' + clientCount,
        v2(scale * (x - HASH_SUFFIX_WIDTH), scale * (manager.space.height - 29)),
        scale,
        align,
        COLORS.BLACK_100,
        10,
        'font_bold',
      ),
    );
    rPackage.push(
      CreateDebugTextPackage(
        ' - ' + clientHash,
        v2(scale * x, scale * (manager.space.height - 29)),
        scale,
        align,
        COLORS.DARKGREY2_100,
      ),
    );
  }

  if (doRedraw) {
    rPackage.push(
      CreateDebugTextPackage(
        'DRAW',
        v2(scale * 40, scale * (manager.space.height - 70)),
        scale,
        align,
        COLORS.RED_SALSA,
        10,
      ),
    );
  }

  rPackage.push(
    CreateDebugTextPackage(
      'pt: ' + manager.performanceTier.toString(),
      v2(scale * 40, scale * (manager.space.height - 40)),
      scale,
      align,
      COLORS.RED_SALSA,
      10,
    ),
  );

  return rPackage;
}

export function createDebugPackage(manager: GameManager, scale: number): RenderPackageItem[] {
  const rPackage: RenderPackageItem[] = [];

  const canvasWidth = manager.mainRoom?.canvas
    ? manager.mainRoom?.canvas.width / manager.mainRoom?.canvas.layer.scale
    : 0;
  const canvasHeight = manager.mainRoom?.canvas
    ? manager.mainRoom?.canvas.height / manager.mainRoom?.canvas.layer.scale
    : 0;
  let debugLines: string[][] = [
    [`Rooms:`, getObjectCountByType(manager, ObjectType.ROOM).toString()],
    [`Entities:`, manager.entities.length.toString()],
    [`Top-Highlight:`, `${manager.cursor.highlightTopEntity?.id}`],
    [`Highlight:`, `${manager.cursor.highlightEntities.map((it) => it.id.toString()).join(', ')}`],
    [`MouseDownOn:`, manager.cursor.mouseDownOnEntity?.id.toString()],
    [`MaybeDragEntity:`, manager.cursor.maybeDragEntity?.id.toString()],
    [`DragEntity:`, manager.cursor.draggedEntity?.id.toString()],
    [`Mouse screen Pos:`, `${manager.mousePos.x.toString()}, ${manager.mousePos.y.toString()}`],

    [
      `Mouse canvas Pos:`,
      manager.mainRoom?.canvas
        ? `${Math.round(manager.mainRoom.canvas.layer.relativeMousePos.x).toString()}, ${Math.round(manager.mainRoom.canvas.layer.relativeMousePos.y).toString()}`
        : '',
    ],

    [
      `Canvas pos:`,
      `${manager.mainRoom?.canvas?.focusPosition.x.toFixed()},${manager.mainRoom?.canvas?.focusPosition.y.toFixed()}`,
    ],
    [`Canvas size:`, `w: ${canvasWidth.toFixed()},h: ${canvasHeight.toFixed()}`],
  ];

  for (const [typeName, set] of manager.registry.byType.entries()) {
    if (set.size > 0) {
      debugLines.push([typeName + ':', set.size.toString()]);
    }
  }

  const distBetweenLines = 3;
  {
    const margin = 20;
    const w = 300;
    const h = 2 * margin + debugLines.length * (10 + distBetweenLines);
    const positionX = (w / 2 + 10) * scale;
    const positionY = (h / 2 + 80) * scale;
    let rect: RenderPackageRect = {
      type: RenderItemType.rect,
      ...EMPTY_RENDER_TRANSFORM,
      scale,
      positionX,
      positionY,
      width: w,
      height: h,
      shapeStyle: { ...SHAPE_STYLES.NONE, fillColor: [180, 180, 180, 210] },
      cornerRadius1: 0,
      cornerRadius2: 0,
      cornerRadius3: 0,
      cornerRadius4: 0,
      shadow: null,
    };
    rPackage.push(rect);

    for (let i = 0; i < debugLines.length; i++) {
      const localPosY = (80 + margin + i * (10 + distBetweenLines)) * scale;

      let color: Color = [0, 0, 0];
      if (debugLines[i][1] === '0' || debugLines[i][1] === 'undefined') {
        debugLines[i][1] = '';
        color = [60, 60, 60];
      }
      for (let c = 0; c < debugLines[i].length; c++) {
        const localPosX = c === 0 ? margin * scale : (margin + 140) * scale;
        let textPos = v2(localPosX, localPosY);
        const textPackage = CreateDebugTextPackage(
          debugLines[i][c],
          textPos,
          scale,
          TextAlignHor.LEFT,
          color,
          10,
        );
        rPackage.push(textPackage);
      }
    }
  }

  return rPackage;
}

export function CreateDebugTextPackage(
  text: string,
  pos: V2,
  scale: number,
  align: TextAlignHor = TextAlignHor.CENTER,
  color: Color = COLORS.BLACK_100,
  size: number = 10,
  fontId: string = defaultFontId,
): RenderPackageText {
  let rText: RenderPackageText = {
    ...EMPTY_RENDER_TRANSFORM,
    type: RenderItemType.text,

    scale,
    positionX: pos.x,
    positionY: pos.y,
    fillColor: color,
    size: size,
    text: text,
    hAlign: align,
    fontId,
    strokeWeight: 0,
    strokeColor: [0, 0, 0],
    shadow: null,
  };

  return rText;
}

export function buildClosedArrow(
  position: V2,
  scale: number,
  tipX: number,
  tipY: number,
  dirX: number,
  dirY: number,
  color: Color,
  lineWeight: number,
): RenderPackageShape {
  const arrowLength = lineWeight * 5;
  const halfWidth = lineWeight * 3;
  const perpX = -dirY;
  const perpY = dirX;
  const baseX = tipX - dirX * arrowLength;
  const baseY = tipY - dirY * arrowLength;

  return {
    type: RenderItemType.shape,
    ...EMPTY_RENDER_TRANSFORM,
    positionX: position.x,
    positionY: position.y,
    scale,
    points: [
      v2(tipX, tipY),
      v2(baseX + perpX * halfWidth, baseY + perpY * halfWidth),
      v2(baseX - perpX * halfWidth, baseY - perpY * halfWidth),
    ],
    fill: true,
    close: true,
    shapeStyle: { ...SHAPE_STYLES.NONE, fillColor: color },
    shadow: null,
  };
}

export function buildOpenArrow(
  position: V2,
  scale: number,
  tipX: number,
  tipY: number,
  dirX: number,
  dirY: number,
  color: Color,
  lineWeight: number,
): RenderPackageShape {
  const armLength = lineWeight * 4;
  const halfWidth = lineWeight * 3;
  const perpX = -dirY;
  const perpY = dirX;
  const baseX = tipX - dirX * armLength;
  const baseY = tipY - dirY * armLength;

  return {
    type: RenderItemType.shape,
    ...EMPTY_RENDER_TRANSFORM,
    positionX: position.x,
    positionY: position.y,
    scale,
    points: [
      v2(baseX + perpX * halfWidth, baseY + perpY * halfWidth),
      v2(tipX, tipY),
      v2(baseX - perpX * halfWidth, baseY - perpY * halfWidth),
    ],
    fill: false,
    close: false,
    shapeStyle: { ...SHAPE_STYLES.NONE, strokeWeight: lineWeight, strokeColor: color },
    shadow: null,
  };
}

export function buildLineDot(
  position: V2,
  scale: number,
  centerX: number,
  centerY: number,
  color: Color,
  lineWeight: number,
): RenderPackageCircle {
  return {
    type: RenderItemType.circle,
    ...EMPTY_RENDER_TRANSFORM,
    positionX: position.x,
    positionY: position.y,
    positionLocalX: centerX,
    positionLocalY: centerY,
    scale,
    radius: lineWeight * 2,
    shapeStyle: { ...SHAPE_STYLES.NONE, fillColor: color },
    shadow: null,
  };
}
